// Standard-size design template presets that map a product to the
// dynamic template PDF endpoint `/api/templates/{W}x{H}.pdf`.
//
// The chip strip on `ProductDetailPage` uses this to give customers a
// one-click download of the correct-sized print-ready template with trim,
// bleed, safe-area and designer-notes baked in.
//
// Roll labels are DELIBERATELY excluded — they already have a client-side
// die-cut template UI (`generateDieTemplate` / `POPULAR_DIE_SIZES`).

const T = (label, w, h, bleed = 0.125) => ({ label, w, h, bleed });

// Rules are matched in order:
//   1. Exact slug match (`bySlug`)
//   2. Slug substring match (`bySlugIncludes`)
//   3. Service category (`byCategory`)
const RULES = {
  bySlug: {
    // Envelopes are unusual — no bleed by convention on a #10 flap.
    "digital-envelopes-10": [T("#10 Envelope · 9.5×4.125", 9.5, 4.125, 0)],
    "banner-retractable-36": [T('36" Retractable · 36×92', 36, 92, 0.25)],
    "standard-retractable-roll-up": [
      T('33.5" Retractable · 33.5×81', 33.5, 81, 0.25),
      T('36" Retractable · 36×92', 36, 92, 0.25),
    ],
    "banner-pull-up-double": [
      T('33.5" Retractable · 33.5×81', 33.5, 81, 0.25),
      T('36" Retractable · 36×92', 36, 92, 0.25),
    ],
    "banner-x-stand": [T("X-Stand · 24×63", 24, 63, 0.25)],
    "banner-flag-feather": [
      T("Feather Flag S · 30×80", 30, 80, 0.25),
      T("Feather Flag L · 30×108", 30, 108, 0.25),
    ],
    "banner-vinyl-outdoor": [
      T("Vinyl 2×4 ft", 24, 48, 0.25),
      T("Vinyl 3×6 ft", 36, 72, 0.25),
      T("Vinyl 4×8 ft", 48, 96, 0.25),
    ],
    "banner-mesh-windload": [
      T("Mesh 3×6 ft", 36, 72, 0.25),
      T("Mesh 4×8 ft", 48, 96, 0.25),
    ],
    "banner-tabletop-tension": [T("Tabletop · 60×36", 60, 36, 0.25)],
    "marketing-yard-signs": [
      T("Yard Sign 18×24", 18, 24, 0.125),
      T("Yard Sign 24×36", 24, 36, 0.125),
    ],
    "marketing-magnets-business": [T("Magnet · 3.5×2", 3.5, 2, 0.125)],
    "marketing-window-clings": [
      T("Cling 8.5×11", 8.5, 11, 0.125),
      T("Cling 12×12", 12, 12, 0.125),
    ],
    "marketing-floor-graphics": [
      T("Floor 12×12", 12, 12, 0.125),
      T("Floor 24×24", 24, 24, 0.125),
    ],
    "marketing-eddm-6.5x9": [T("EDDM · 6.5×9", 6.5, 9, 0.125)],
    "marketing-eddm-8.5x11": [T("EDDM Mega · 8.5×11", 8.5, 11, 0.125)],
    "marketing-rack-cards-tri": [T("Rack Card · 4×9 (flat)", 4, 9, 0.125)],
    "marketing-door-hangers-jumbo": [T("Door Hanger · 5.5×8.5", 5.5, 8.5, 0.125)],
    "digital-postcards-4x6": [T("Postcard · 4×6", 4, 6, 0.125)],
    "digital-postcards-5x7": [T("Postcard · 5×7", 5, 7, 0.125)],
    "digital-flyers-half": [T("Half Flyer · 5.5×8.5", 5.5, 8.5, 0.125)],
    "digital-flyers-letter": [T("Letter Flyer · 8.5×11", 8.5, 11, 0.125)],
    "digital-letterheads": [T("Letterhead · 8.5×11", 8.5, 11, 0)],
    "digital-notepads": [
      T("Notepad · 5.5×8.5", 5.5, 8.5, 0.125),
      T("Notepad · 8.5×11", 8.5, 11, 0.125),
    ],
    "digital-door-hangers": [T("Door Hanger · 4.25×11", 4.25, 11, 0.125)],
    "digital-table-tents": [T("Table Tent · 4×6", 4, 6, 0.125)],
    "offset-postcards-jumbo": [T("Jumbo Postcard · 6×11", 6, 11, 0.125)],
    "offset-rack-cards": [T("Rack Card · 4×9 (flat)", 4, 9, 0.125)],
    "offset-product-sheets": [T("Sell Sheet · 8.5×11", 8.5, 11, 0.125)],
    "offset-bi-fold-brochures": [
      T("Bi-Fold Letter · 8.5×11 (flat)", 8.5, 11, 0.125),
      T("Bi-Fold Tabloid · 11×17 (flat)", 11, 17, 0.125),
    ],
    "offset-tri-fold-brochures": [T("Tri-Fold · 8.5×11 (flat)", 8.5, 11, 0.125)],
    "offset-letterhead-2c": [T("Letterhead · 8.5×11", 8.5, 11, 0)],
    // Rigid substrates & foam / gator / PVC boards — 3/16" foam board is
    // most commonly cut at 24×36 or 18×24 for retail displays.
    "foam-board": [
      T("Foam Board · 18×24", 18, 24, 0.125),
      T("Foam Board · 24×36", 24, 36, 0.125),
      T("Foam Board · 36×48", 36, 48, 0.25),
    ],
    "gator-board": [
      T("Gator Board · 24×36", 24, 36, 0.125),
      T("Gator Board · 36×48", 36, 48, 0.25),
      T("Gator Board · 48×96", 48, 96, 0.25),
    ],
    "pvc-board": [
      T("PVC · 24×36", 24, 36, 0.125),
      T("PVC · 48×96", 48, 96, 0.25),
    ],
    "aluminum-signs": [
      T("Aluminum · 18×24", 18, 24, 0.125),
      T("Aluminum · 24×36", 24, 36, 0.125),
    ],
  },
  // Substring matches — used for the many business-card / postcard variants.
  bySlugIncludes: [
    { needle: "business-card", presets: [T("Business Card · 3.5×2", 3.5, 2, 0.125)] },
    { needle: "semi-gloss",    presets: [T("Business Card · 3.5×2", 3.5, 2, 0.125)] },
    { needle: "standard14pt",  presets: [T("Business Card · 3.5×2", 3.5, 2, 0.125)] },
    { needle: "postcard",      presets: [
      T("Postcard · 4×6", 4, 6, 0.125),
      T("Postcard · 5×7", 5, 7, 0.125),
      T("Postcard · 6×9", 6, 9, 0.125),
    ] },
    { needle: "foam",  presets: [
      T('Foam Board · 18×24', 18, 24, 0.125),
      T('Foam Board · 24×36', 24, 36, 0.125),
      T('Foam Board · 36×48', 36, 48, 0.25),
    ] },
    { needle: "poster", presets: [
      T('Poster · 11×17', 11, 17, 0.125),
      T('Poster · 18×24', 18, 24, 0.125),
      T('Poster · 24×36', 24, 36, 0.125),
    ] },
    { needle: "gator", presets: [
      T('Gator Board · 24×36', 24, 36, 0.125),
      T('Gator Board · 48×96', 48, 96, 0.25),
    ] },
    { needle: "coroplast", presets: [
      T('Coroplast · 18×24', 18, 24, 0.125),
      T('Coroplast · 24×36', 24, 36, 0.125),
    ] },
    { needle: "acrylic", presets: [
      T('Acrylic · 12×18', 12, 18, 0),
      T('Acrylic · 24×36', 24, 36, 0),
    ] },
  ],
  byCategory: {
    // Presentation folders open flat to ~18×12, but the die itself varies.
    // Safer to omit here and let sales send the customer the exact die.
  },
};

// Large-format products (banners, retractables, flags, displays) sell in
// their own named sizes, so their templates are DERIVED from the product's
// size list — every size a customer can order has a matching template.
const TEMPLATE_MIN_IN = 0.5;
const TEMPLATE_MAX_IN = 240;

const LARGE_FORMAT_CATEGORIES = new Set(["retractables-banners"]);

export function parseSizeToInches(label) {
  const raw = String(label || "").replace(/[–—]/g, "-");
  const m = raw.match(
    /(\d+(?:\.\d+)?)\s*(ft|feet|foot|in|inch|inches|"|''|')?\s*[wWhH]?\s*(?:x|×|X)\s*(\d+(?:\.\d+)?)\s*(ft|feet|foot|in|inch|inches|"|''|')?/,
  );
  if (!m) return null;
  const mult = (unit) => (/^(ft|feet|foot|')$/i.test(unit || "") ? 12 : 1);
  const fallback = /\b(ft|feet|foot)\b|'/i.test(raw) && !/"|''|\bin(ch)?\b/i.test(raw) ? 12 : 1;
  const w = parseFloat(m[1]) * (m[2] ? mult(m[2]) : (m[4] ? mult(m[4]) : fallback));
  const h = parseFloat(m[3]) * (m[4] ? mult(m[4]) : (m[2] ? mult(m[2]) : fallback));
  if (!Number.isFinite(w) || !Number.isFinite(h)) return null;
  if (w < TEMPLATE_MIN_IN || h < TEMPLATE_MIN_IN || w > TEMPLATE_MAX_IN || h > TEMPLATE_MAX_IN) return null;
  return { w: Math.round(w * 100) / 100, h: Math.round(h * 100) / 100 };
}

function derivePresetsFromSizes(product) {
  const out = [];
  const seen = new Set();
  for (const s of product.sizes || []) {
    const name = String((s && s.name) || s || "").trim();
    const dims = parseSizeToInches(name);
    if (!dims) continue;
    const key = `${dims.w}x${dims.h}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const bleed = Math.max(dims.w, dims.h) >= 24 ? 0.25 : 0.125;
    const nameHasSize = String(product.name || "").toLowerCase().includes(name.toLowerCase());
    out.push({
      // `label` is printed inside the PDF; `short` is the row title on the page.
      label: nameHasSize ? String(product.name) : `${product.name} · ${name}`,
      short: name,
      w: dims.w, h: dims.h, bleed,
    });
    if (out.length >= 12) break;
  }
  return out;
}

// Static, hand-built template files (dielines, tent canopies…) attached to a
// product either via `product.template_files` [{label, url}] in the DB or the
// FILE_TEMPLATES map below. They take precedence over generated presets.
const TENT_GUIDE = { label: "Installation Guide · Event Tent (PDF)", file: "/templates/tents/installation-guide-event-tent.pdf", guide: true };
const T15 = "/templates/tents/15ft-tent-";
const FILE_TEMPLATES = {
  "10x10-custom-event-tent": [
    { label: "10' × 10' Tent Canopy · full dieline (PDF)", file: "/templates/tents/10x10-custom-event-tent.pdf", w: 120, h: 120 },
    TENT_GUIDE,
  ],
  "10x15-custom-event-tent": [
    { label: "15ft Event Tent · Canopy Long Side", file: `${T15}canopy-long-side.pdf`, group: "15ft Event Tent" },
    { label: "15ft Tent Full Wall · Short Side", file: `${T15}full-wall-short-side.pdf`, group: "15ft Tent Full Wall (Short Side)" },
    { label: "15ft Tent Full Wall · Long Side", file: `${T15}full-wall-long-side.pdf`, group: "15ft Tent Full Wall (Long Side)" },
    { label: "15ft Tent Half Wall", file: `${T15}half-wall.pdf`, group: "15ft Tent Half Wall" },
    TENT_GUIDE,
  ],
  "10x20-custom-event-tent": [TENT_GUIDE],
  "stretch-table-throw": [
    { label: "6ft Stretch Table Cover · print template (PDF)", file: "/templates/table-covers/6ft-stretch-table-cover.pdf", group: "6ft Stretch Table Cover" },
    { label: "8ft Stretch Table Cover · print template (PDF)", file: "/templates/table-covers/8ft-stretch-table-cover.pdf", group: "8ft Stretch Table Cover" },
  ],
};

export function getTemplatePresetsForProduct(product) {
  if (!product) return [];
  const slug = String(product.slug || "").toLowerCase();
  const files = [
    ...(Array.isArray(product.template_files) ? product.template_files : []).map((t, i) => ({ label: t.label || `Template ${i + 1}`, file: t.url, w: t.w || 0, h: t.h || i })),
    ...(FILE_TEMPLATES[slug] || []),
  ].filter((t) => t.file);
  if (files.some((t) => !t.guide)) return files;
  if (files.length) return [...generatedPresets(product, slug), ...files];
  return generatedPresets(product, slug);
}

function generatedPresets(product, slug) {
  const cat = String(product.service_category || "").toLowerCase();
  if (LARGE_FORMAT_CATEGORIES.has(cat)) {
    const derived = derivePresetsFromSizes(product);
    if (derived.length) return derived;
  }
  if (RULES.bySlug[slug]) return RULES.bySlug[slug];
  for (const { needle, presets } of RULES.bySlugIncludes) {
    if (slug.includes(needle)) return presets;
  }
  if (RULES.byCategory[cat]) return RULES.byCategory[cat];
  return [];
}

// Build the download URL for the dynamic template endpoint. The backend
// tolerates `WxH.pdf` in inches; bleed and label are optional query params.
export function templateHref(apiBase, slug, preset) {
  if (preset.file) return preset.file;
  const dims = `${preset.w}x${preset.h}.pdf`;
  const params = new URLSearchParams({
    label: preset.label,
    bleed: String(preset.bleed ?? 0.125),
  });
  return `${apiBase}/templates/${dims}?${params.toString()}`;
}
